import React, { useState, useCallback } from 'react';
import {
  Sparkles, X, Loader2, CheckCircle, AlertTriangle, Copy,
  FileText, Shield, Link2, ListChecks, RefreshCw, ChevronDown,
  ChevronUp, Lightbulb, HelpCircle, Target, Zap
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { WidgetErrorBoundary } from './ErrorBoundary';

// ==========================================
// SUGGESTION TYPES CONFIG
// ==========================================

const SUGGESTION_TYPES = {
  clarity: {
    icon: Lightbulb,
    label: 'Clarity',
    description: 'Improve requirement clarity and precision',
    color: 'text-amber-600 bg-amber-50',
  },
  acceptance_criteria: {
    icon: ListChecks,
    label: 'Acceptance Criteria',
    description: 'Generate acceptance criteria',
    color: 'text-green-600 bg-green-50',
  },
  risks: {
    icon: AlertTriangle,
    label: 'Risks',
    description: 'Identify delivery risks',
    color: 'text-red-600 bg-red-50',
  },
  constraints: {
    icon: Shield,
    label: 'Constraints',
    description: 'Detect constraints',
    color: 'text-blue-600 bg-blue-50',
  },
  dependencies: {
    icon: Link2,
    label: 'Dependencies',
    description: 'Find dependencies',
    color: 'text-purple-600 bg-purple-50',
  },
  quality_explanation: {
    icon: HelpCircle,
    label: 'Explain Quality',
    description: 'Explain quality flags',
    color: 'text-cyan-600 bg-cyan-50',
  },
};

// ==========================================
// SUGGESTION CARD COMPONENT
// ==========================================

const SuggestionCard = ({ type, data, onApply, onDiscard, isApplying }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const config = SUGGESTION_TYPES[type] || SUGGESTION_TYPES.clarity;
  const Icon = config.icon;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const renderContent = () => {
    if (!data) return null;

    switch (type) {
      case 'clarity':
        return (
          <div className="space-y-3">
            {data.rewritten_requirement && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Suggested Rewrite</p>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-800">{data.rewritten_requirement}</p>
                  <button
                    onClick={() => copyToClipboard(data.rewritten_requirement)}
                    className="mt-2 text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
              </div>
            )}
            {data.issues_identified?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Issues Identified</p>
                <ul className="space-y-1">
                  {data.issues_identified.map((issue, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                      <span className="text-gray-700">{issue}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {data.clarifying_questions?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Questions to Answer</p>
                <ul className="space-y-1">
                  {data.clarifying_questions.map((q, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <HelpCircle className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" />
                      <span className="text-gray-700">{q}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'acceptance_criteria':
        return (
          <div className="space-y-2">
            {data.acceptance_criteria?.map((ac, i) => (
              <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    ac.type === 'gherkin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {ac.type}
                  </span>
                  <button
                    onClick={() => onApply?.({ type: 'ac', data: ac })}
                    disabled={isApplying}
                    className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1"
                  >
                    <CheckCircle className="w-3 h-3" /> Apply
                  </button>
                </div>
                {ac.type === 'gherkin' ? (
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium text-purple-600">Given</span> {ac.given}</p>
                    <p><span className="font-medium text-blue-600">When</span> {ac.when}</p>
                    <p><span className="font-medium text-green-600">Then</span> {ac.then}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-700">{ac.content}</p>
                )}
              </div>
            ))}
          </div>
        );

      case 'risks':
        return (
          <div className="space-y-2">
            {data.suggested_risks?.map((risk, i) => (
              <div key={i} className="p-3 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
                    {risk.category}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      L: {risk.likelihood} / I: {risk.impact}
                    </span>
                    <button
                      onClick={() => onApply?.({ type: 'risk', data: risk })}
                      disabled={isApplying}
                      className="text-xs text-green-600 hover:text-green-700"
                    >
                      Apply
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-700 mb-2">{risk.description}</p>
                {risk.mitigation_suggestion && (
                  <p className="text-xs text-gray-500">
                    <span className="font-medium">Mitigation:</span> {risk.mitigation_suggestion}
                  </p>
                )}
              </div>
            ))}
          </div>
        );

      case 'constraints':
        return (
          <div className="space-y-2">
            {data.suggested_constraints?.map((constraint, i) => (
              <div key={i} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                    {constraint.category}
                  </span>
                  <button
                    onClick={() => onApply?.({ type: 'constraint', data: constraint })}
                    disabled={isApplying}
                    className="text-xs text-green-600 hover:text-green-700"
                  >
                    Apply
                  </button>
                </div>
                <p className="text-sm text-gray-700">{constraint.description}</p>
                {constraint.impact && (
                  <p className="text-xs text-gray-500 mt-1">
                    <span className="font-medium">Impact:</span> {constraint.impact}
                  </p>
                )}
              </div>
            ))}
          </div>
        );

      case 'dependencies':
        return (
          <div className="space-y-2">
            {data.suggested_dependencies?.map((dep, i) => (
              <div key={i} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                    {dep.type}
                  </span>
                  <div className="flex items-center gap-2">
                    {dep.blocking && (
                      <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                        Blocking
                      </span>
                    )}
                    <button
                      onClick={() => onApply?.({ type: 'dependency', data: dep })}
                      disabled={isApplying}
                      className="text-xs text-green-600 hover:text-green-700"
                    >
                      Apply
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-700">{dep.description}</p>
              </div>
            ))}
          </div>
        );

      case 'quality_explanation':
        return (
          <div className="space-y-3">
            {data.explanations?.map((exp, i) => (
              <div key={i} className="p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                <p className="text-sm font-medium text-cyan-700 mb-1">{exp.flag}</p>
                <p className="text-sm text-gray-700 mb-1">{exp.meaning}</p>
                <p className="text-xs text-gray-500">{exp.how_to_fix}</p>
              </div>
            ))}
            {data.overall_assessment && (
              <div className="p-3 bg-gray-100 rounded-lg">
                <p className="text-xs font-medium text-gray-500 mb-1">Overall Assessment</p>
                <p className="text-sm text-gray-700">{data.overall_assessment}</p>
              </div>
            )}
          </div>
        );

      default:
        return (
          <pre className="text-xs text-gray-600 bg-gray-50 p-3 rounded overflow-auto">
            {JSON.stringify(data, null, 2)}
          </pre>
        );
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between p-3 ${config.color} transition-colors`}
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" />
          <span className="font-medium text-sm">{config.label}</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>
      
      {isExpanded && (
        <div className="p-4 bg-white">
          {renderContent()}
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN AI REQUIREMENT ASSISTANT COMPONENT
// ==========================================

const AIRequirementAssistant = ({
  isOpen,
  onClose,
  requirement,
  tenantId,
  onApplySuggestion,
}) => {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState(null);

  const fetchSuggestions = useCallback(async (type = 'all') => {
    if (!requirement) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let response;
      const basePayload = {
        requirement_id: requirement.id,
        requirement_name: requirement.name,
        requirement_description: requirement.description || '',
        category: requirement.category || '',
        hierarchy_type: requirement.hierarchy_type || 'requirement',
        existing_acceptance_criteria: requirement.acceptance_criteria || [],
        existing_risks: requirement.risks || [],
        existing_constraints: requirement.constraints || [],
        existing_dependencies: requirement.dependencies || [],
        tenant_id: tenantId,
        suggestion_type: type,
      };

      if (type === 'all') {
        response = await api.post('/api/ai-assist/suggest/all', basePayload);
        setSuggestions(response.data.suggestions);
      } else if (type === 'clarity') {
        response = await api.post('/api/ai-assist/suggest/clarity', {
          requirement_text: `${requirement.name}: ${requirement.description || ''}`,
          category: requirement.category,
          tenant_id: tenantId,
        });
        setSuggestions(prev => ({ ...prev, clarity: response.data.suggestions }));
      } else if (type === 'acceptance_criteria') {
        response = await api.post('/api/ai-assist/suggest/acceptance-criteria', {
          requirement_name: requirement.name,
          requirement_description: requirement.description || '',
          category: requirement.category,
          format: 'both',
          count: 3,
          tenant_id: tenantId,
        });
        setSuggestions(prev => ({ ...prev, acceptance_criteria: response.data.suggestions }));
      } else {
        response = await api.post(`/api/ai-assist/suggest/${type}`, basePayload);
        setSuggestions(prev => ({ ...prev, [type]: response.data.suggestions }));
      }
      
      toast.success('AI suggestions generated');
    } catch (err) {
      console.error('Failed to fetch AI suggestions:', err);
      setError(err.response?.data?.detail || 'Failed to generate suggestions');
      toast.error('Failed to generate suggestions');
    } finally {
      setLoading(false);
    }
  }, [requirement, tenantId]);

  const handleApply = useCallback(async (item) => {
    if (!onApplySuggestion) return;
    
    setIsApplying(true);
    try {
      await onApplySuggestion(item);
      toast.success('Suggestion applied');
    } catch (err) {
      toast.error('Failed to apply suggestion');
    } finally {
      setIsApplying(false);
    }
  }, [onApplySuggestion]);

  if (!isOpen) return null;

  return (
    <WidgetErrorBoundary widgetName="AI Assistant">
      <>
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black/30 z-40"
          onClick={onClose}
        />
        
        {/* Sidebar Modal */}
        <div className="fixed right-0 top-0 h-full w-[480px] bg-white shadow-2xl z-50 flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-600 to-indigo-600">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-white">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-semibold">AI Requirement Assistant</h2>
                  <p className="text-xs text-white/80">PMO-Safe Suggestions</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Requirement Info */}
          <div className="px-6 py-4 bg-gray-50 border-b">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Target className="w-4 h-4 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{requirement?.name}</p>
                <p className="text-sm text-gray-500 truncate">
                  {requirement?.description || 'No description'}
                </p>
                {requirement?.category && (
                  <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                    {requirement.category}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="px-6 py-3 border-b flex items-center gap-2 flex-wrap">
            <button
              onClick={() => fetchSuggestions('all')}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
              data-testid="ai-suggest-all-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
              Generate All
            </button>
            {Object.entries(SUGGESTION_TYPES).slice(0, 4).map(([key, config]) => (
              <button
                key={key}
                onClick={() => fetchSuggestions(key)}
                disabled={loading}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded border ${config.color} hover:opacity-80 disabled:opacity-50`}
              >
                {React.createElement(config.icon, { className: 'w-3 h-3' })}
                {config.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading && !suggestions && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600 mb-4" />
                <p className="text-sm text-gray-600">Generating suggestions...</p>
                <p className="text-xs text-gray-400 mt-1">This may take a few seconds</p>
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 text-red-600 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium text-sm">Error</span>
                </div>
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={() => fetchSuggestions('all')}
                  className="mt-3 text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" /> Try again
                </button>
              </div>
            )}

            {!loading && !suggestions && !error && (
              <div className="text-center py-12">
                <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-medium mb-2">Ready to assist</p>
                <p className="text-sm text-gray-500 mb-4">
                  Click "Generate All" to get AI-powered suggestions for this requirement
                </p>
                <button
                  onClick={() => fetchSuggestions('all')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <Zap className="w-4 h-4" />
                  Generate Suggestions
                </button>
              </div>
            )}

            {suggestions && (
              <div className="space-y-4">
                {suggestions.clarity && (
                  <SuggestionCard
                    type="clarity"
                    data={suggestions.clarity}
                    onApply={handleApply}
                    isApplying={isApplying}
                  />
                )}
                {suggestions.acceptance_criteria && (
                  <SuggestionCard
                    type="acceptance_criteria"
                    data={suggestions.acceptance_criteria}
                    onApply={handleApply}
                    isApplying={isApplying}
                  />
                )}
                {suggestions.risks && (
                  <SuggestionCard
                    type="risks"
                    data={suggestions.risks}
                    onApply={handleApply}
                    isApplying={isApplying}
                  />
                )}
                {suggestions.constraints && (
                  <SuggestionCard
                    type="constraints"
                    data={suggestions.constraints}
                    onApply={handleApply}
                    isApplying={isApplying}
                  />
                )}
                {suggestions.dependencies && (
                  <SuggestionCard
                    type="dependencies"
                    data={suggestions.dependencies}
                    onApply={handleApply}
                    isApplying={isApplying}
                  />
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t bg-gray-50">
            <p className="text-xs text-gray-500 text-center">
              PMO-Safe AI • No commercial advice • Governance-focused only
            </p>
          </div>
        </div>
      </>
    </WidgetErrorBoundary>
  );
};

export default AIRequirementAssistant;
